import { add, getDay, startOfDay, startOfMinute } from "date-fns";
import { action, makeAutoObservable, reaction, runInAction } from "mobx";
import z from "zod";
import { apiClient } from "../../common/apiClient";
import { logger } from "../../common/logger";
import { platformRoutes, resolvePlatformRequestUrl } from "../../common/platformRoutes";
import type { BaseStoreType } from "../../common/types/BaseStoreType";
import type { DateInterval, DeliveryWindowData } from "../../common/types/deliveryTypes";
import { type Delivery, type DeliveryStatus, ZDelivery } from "../../common/zodSchemas/delivery";
import type { ServerEventListener } from "../../common/zodSchemas/serverEvents";
import theme from "../../themes/theme";
import authStore from "../auth/authStore";
import deliveryWindowStore from "../deliveryDates/deliveryWindowStore";
import serverEventStore from "../serverEventsStore";
import { type DSPOrderWithParsedDeliveryDate, fetchOrdersForDeliveryInterval } from "./deliveryApi";
import {
   getDeliveryBannerTitleFromStatus,
   getFormattedDeliveryInfoText,
   getFormattedRoutePositionText,
   getHighestClientDeliveryOrderStatus,
   getIntervalForDateAndTimes,
   getIntervalFromEtaWindow
} from "./deliveryUtils";

export type ClientDeliveryOrderStatus = "RECEIVED" | "PACKING" | "PACKED" | "INVOICED" | "DELETED" | "CANNOT_DELIVER";
export type ClientDeliveryStatus = DeliveryStatus | ClientDeliveryOrderStatus | "NO_ORDERS";

export type ClientDelivery = {
   deliveryInterval: DateInterval | null;
   deliveredTime: Date | null;
   status: ClientDeliveryStatus;
   nextDeliveryDate: Date | null;
   nextDeliveryOrders: DSPOrderWithParsedDeliveryDate[];
   nextDeliveryWindowData: DeliveryWindowData | null;
   shippingAddress: string[];
   eta: Date | null;
   stopNumber: number | null;
   currentStopNumber: number | null;
   deliveryBannerTitle: string;
   deliveryBannerInfo: string;
   deliveryBannerRoutePosition: string;
};

type DeliveryStore = BaseStoreType & {
   dataOwner: string | null;
   rawDelivery: Delivery | null;
   orders: DSPOrderWithParsedDeliveryDate[];
   delivery: ClientDelivery | null;
   windowTableData: DeliveryWindowData[];
   currentDateTime: Date;
   currentDate: Date;
   init(): void;
   fillStore: () => Promise<void>;
   fetchLatestDelivery: () => Promise<Delivery | null>;
};

const handleUpdatedDelivery: ServerEventListener = async (message) => {
   const delivery = await deliveryStore.fetchLatestDelivery();
   if (message.type === "delivery_status" && "customerNumber" in message && message.customerNumber === deliveryStore.dataOwner) {
      runInAction(() => {
         deliveryStore.rawDelivery = delivery;
      });
   }
   if (message.type === "order_delivered") {
      const interval = deliveryStore.delivery?.deliveryInterval;
      const { dataOwner } = deliveryStore;
      if (interval && dataOwner) {
         const orders = await fetchOrdersForDeliveryInterval(interval, theme.m3CompanyNumber, dataOwner);
         runInAction(() => {
            if (deliveryStore.dataOwner === dataOwner) {
               deliveryStore.orders = orders;
            }
         });
      }
   }
};

const deliveryStore: DeliveryStore = makeAutoObservable({
   dataOwner: null,
   rawDelivery: null,
   orders: [],
   currentDateTime: startOfMinute(new Date()),
   get currentDate() {
      return startOfDay(deliveryStore.currentDateTime);
   },
   get windowTableData() {
      if (deliveryWindowStore.windowsTableData.progress === "dataAvailable") {
         return deliveryWindowStore.windowsTableData.data;
      }
      return [];
   },
   delivery: {
      get deliveryBannerInfo() {
         const { delivery, currentDate } = deliveryStore;
         if (delivery) {
            return getFormattedDeliveryInfoText(delivery, currentDate);
         }
         return "";
      },
      get deliveryBannerRoutePosition() {
         const { delivery } = deliveryStore;
         return delivery ? getFormattedRoutePositionText(delivery) : "";
      },
      get stopNumber() {
         return deliveryStore.rawDelivery?.stopNumber ?? null;
      },
      get currentStopNumber() {
         return deliveryStore.rawDelivery?.currentStopNumber ?? null;
      },
      get shippingAddress() {
         return authStore.getCurrentCompany()?.shippingAddress || [];
      },
      get deliveryBannerTitle() {
         const { delivery } = deliveryStore;
         if (delivery) {
            const { status } = delivery;
            return getDeliveryBannerTitleFromStatus(status);
         }
         return "";
      },

      get deliveredTime() {
         return deliveryStore.rawDelivery?.deliveredTime
            ? startOfMinute(new Date(deliveryStore.rawDelivery.deliveredTime))
            : null;
      },
      get deliveryInterval() {
         if (deliveryStore.rawDelivery?.status === "DELIVERED") {
            return null;
         }
         const etaWindow = getIntervalFromEtaWindow(
            deliveryStore.rawDelivery?.etaWindowFrom,
            deliveryStore.rawDelivery?.etaWindowTo
         );
         if (etaWindow) {
            return etaWindow;
         }
         if (deliveryStore.delivery?.nextDeliveryWindowData && deliveryStore.delivery?.nextDeliveryDate) {
            const firstPeriod = deliveryStore.delivery.nextDeliveryWindowData.periods[0];
            if (!firstPeriod) {
               return null;
            }
            return getIntervalForDateAndTimes(
               deliveryStore.delivery.nextDeliveryDate,
               firstPeriod // we only support one delivery window period per day, so we can just take the first one.
            );
         }
         return null;
      },
      get eta() {
         if (!deliveryStore.rawDelivery?.adjustedEta) {
            return null;
         }
         return new Date(deliveryStore.rawDelivery.adjustedEta);
      },
      get status() {
         if (deliveryStore.orders.length) {
            const orderStatus = getHighestClientDeliveryOrderStatus(deliveryStore.orders) || "NO_ORDERS";
            const liveStatus = deliveryStore.rawDelivery?.status;
            return liveStatus || orderStatus;
         }
         return "NO_ORDERS";
      },
      get nextDeliveryWindowData() {
         const nextDeliveryDate = deliveryStore.delivery?.nextDeliveryDate;
         if (!nextDeliveryDate) {
            return null;
         }
         const nextDeliveryDayOfWeek = getDay(nextDeliveryDate);
         return (
            deliveryStore.windowTableData.find((windowData) => {
               return windowData.day === nextDeliveryDayOfWeek;
            }) || null
         );
      },
      get nextDeliveryDate() {
         if (deliveryStore.rawDelivery?.adjustedEta) {
            const deliveryDate = startOfDay(new Date(deliveryStore.rawDelivery.adjustedEta));
            if (deliveryDate.getTime() >= deliveryStore.currentDate.getTime()) {
               return deliveryDate;
            }
         }
         const firstOrder = deliveryStore.orders[0];
         if (!firstOrder?.parsedDeliveryDate) {
            return null;
         }
         return startOfDay(firstOrder.parsedDeliveryDate);
      },
      get nextDeliveryOrders() {
         const nextDeliveryDate = deliveryStore.delivery?.nextDeliveryDate;
         if (!nextDeliveryDate) {
            return [];
         }
         return deliveryStore.orders.filter((order) => {
            const { orderNumber } = order;
            if (orderNumber && deliveryStore.rawDelivery?.orderNumbers) {
               return deliveryStore.rawDelivery.orderNumbers.includes(orderNumber);
            }
            if (!order.parsedDeliveryDate) {
               return false;
            }
            return order.parsedDeliveryDate.getTime() === nextDeliveryDate.getTime();
         });
      }
   },
   init: () => {
      if (authStore.isLoggedIn()) {
         deliveryStore.fillStore();
      }
      reaction(
         () => authStore.isLoggedIn(),
         (isloggedIn) => {
            if (isloggedIn) {
               deliveryStore.fillStore();
            } else {
               deliveryStore.clearCompanySpecificData();
            }
         }
      );

      setInterval(
         action(() => {
            deliveryStore.currentDateTime = startOfMinute(new Date());
         }),
         60 * 1000
      );
   },
   fetchLatestDelivery: async () => {
      if (!authStore.isLoggedIn() || !authStore.currentCompany) {
         return null;
      }
      const statusUrl = resolvePlatformRequestUrl(platformRoutes.deliveryStatus, {
         storeId: theme.storeId,
         customerNumber: authStore.currentCompany
      });
      try {
         const delivery: Delivery = await apiClient(statusUrl, authStore.getSessionToken()).get().json();

         logger.log("delivery", "Fetched latest delivery status from backend: ", delivery);

         const validation = z.safeParse(ZDelivery, delivery);

         const deliveryValid = validation.success;

         if (!deliveryValid) {
            logger.error("delivery", "Latest delivery data from backend failed validation: ", validation.error);
            return null;
         }

         if (delivery.status === "FAILED") {
            return null; // we can show this in future, for now we use this status to simulate no data available.
         }
         const timepoint = delivery.adjustedEta || delivery.deliveredTime || delivery.lastUpdated;

         if (!timepoint) {
            return null;
         }

         const dayOfDeliveryMs = startOfDay(new Date(timepoint)).getTime();

         if (Number.isNaN(dayOfDeliveryMs) || dayOfDeliveryMs < deliveryStore.currentDate.getTime()) {
            return null;
         }

         return delivery;
      } catch (_err) {
         return null;
      }
   },
   fillStore: async () => {
      const { currentCompany } = authStore;
      if (!currentCompany) {
         deliveryStore.clearCompanySpecificData();
         return;
      }
      runInAction(() => {
         deliveryStore.dataOwner = currentCompany;
      });

      const delivery = await deliveryStore.fetchLatestDelivery();
      runInAction(() => {
         deliveryStore.rawDelivery = delivery;
      });
      const today = startOfDay(new Date());
      const twoWeeksFromNow = add(today, { days: 14 });
      const interval: DateInterval = {
         from: today,
         to: twoWeeksFromNow
      };
      const orders = await fetchOrdersForDeliveryInterval(interval, theme.m3CompanyNumber, currentCompany);

      if (deliveryWindowStore.windowsTableData.progress === "defaultData") {
         await deliveryWindowStore.fetchWindows();
      }
      runInAction(() => {
         deliveryStore.orders = orders;
      });
      serverEventStore.removeListener("delivery_status", handleUpdatedDelivery);
      serverEventStore.removeListener("order_delivered", handleUpdatedDelivery);
      serverEventStore.addListener("delivery_status", handleUpdatedDelivery);
      serverEventStore.addListener("order_delivered", handleUpdatedDelivery);
   },
   clearCompanySpecificData: () => {
      deliveryStore.dataOwner = null;
      deliveryStore.rawDelivery = null;
      deliveryStore.orders = [];
   }
}) satisfies DeliveryStore;

export default deliveryStore;
